import { Check } from '@untitled-ui/icons-react';
import { twMerge } from 'tailwind-merge';

import { type Currency, SubscriptionPaymentCycle } from '../entity/api/organization-billing';
import { getQueryClient } from '../sdk/request';
import { getAuthUserFromCache } from '../services/storage';
import { tNamespace } from '../services/translation';
import { formatCurrency } from '../shared/currency';
import { Badge } from './badge';
import { Button } from './buttons/button';

const t = tNamespace('components.plan-card');
const tBilling = tNamespace('app.billing.general');

export type PlanCardExtendedProps = {
  planId: string;
  title: string;
  originalPrice: number;
  currentPrice: number;
  paymentCycle: SubscriptionPaymentCycle;
  currency: Currency;
  discountPercentage?: number;
  isBestValue?: boolean;
  isCurrentPlan?: boolean; // Shows "CURRENT PLAN" badge, hides button

  ctaText?: string; // Optional since current plan doesn't need it
  ctaSubLabel?: string; // Optional sub-label below CTA (e.g. "Effective immediately")
  onCtaClick?: () => void;
  ctaLoading?: boolean;
  // Note: ctaVariant is inferred automatically:
  //   - isCurrentPlan = true → no button
  //   - isBestValue = true → "accent"
  //   - default → "outline"

  // Features list
  features?: string[]; // Array of feature strings, if empty/undefined, section not rendered
  className?: string;
};

export function PlanCardExtended(props: PlanCardExtendedProps) {
  const {
    title,
    currentPrice,
    paymentCycle,
    discountPercentage = 0,
    isBestValue = false,
    isCurrentPlan = false,
    className,
    currency,
    ctaText,
    ctaSubLabel,
    onCtaClick,
    ctaLoading,
    features,
  } = props;

  const queryClient = getQueryClient();
  const currentUser = getAuthUserFromCache(queryClient);
  const userLanguage = currentUser?.language;

  const hasDiscount = discountPercentage > 0;

  const buttonVariant = isBestValue ? 'accent' : 'outline';

  const checkmarkColorClass = isBestValue ? 'text-branding-accent-600' : 'text-dark-gray';

  return (
    <div
      className={twMerge(
        'relative flex w-full flex-col justify-between rounded-lg border border-branding-gray bg-white',
        isCurrentPlan ? 'bg-branding-light-gray' : '',
        className
      )}
    >
      {isCurrentPlan ? (
        <div className="absolute left-1/2 top-0 flex -translate-x-1/2 -translate-y-1/2 justify-center py-2">
          <Badge color="lavander" size="sm">
            <span className="font-body-4 uppercase text-lavander-800">{t('currentPlan')}</span>
          </Badge>
        </div>
      ) : null}

      {/* Top Section - Title, Price, Discount */}
      <div className="relative flex flex-col items-center gap-3 overflow-hidden p-4 pb-8 pt-10 text-center">
        {isBestValue && !isCurrentPlan ? (
          <div className="absolute right-0 top-0">
            <div className="absolute -right-[20px] top-3 origin-center rotate-45">
              <span className="block h-5 whitespace-nowrap bg-branding-accent-600 px-4 text-[10px] font-semibold leading-5 text-white">
                {t('bestValue')}
              </span>
            </div>
          </div>
        ) : null}
        {/* Plan Title */}
        <h3 className="font-body-2-sb flex gap-1 uppercase text-branding-accent">{title}</h3>
        {/* Pricing */}
        <div className="flex flex-col gap-3">
          <div className="flex w-full items-center items-baseline justify-center gap-1">
            <span className="font-display-1 text-dark-gray">
              {formatCurrency(currentPrice, currency, userLanguage)}
            </span>
            <span className="font-display-4 text-dark-gray">/mo</span>
          </div>
          <span className="font-body-2-r text-gray">
            {paymentCycle === SubscriptionPaymentCycle.Year
              ? t('billedYearly')
              : t('billedMonthly')}
          </span>
        </div>

        {/* Discount Badge */}
        {hasDiscount ? (
          <Badge color="accent" size="sm" className="font-body-3-m w-fit">
            {tBilling('percentOff', { percent: discountPercentage })}
          </Badge>
        ) : null}
      </div>

      {/* CTA Button - Only show if not current plan */}
      {!isCurrentPlan && ctaText != null && ctaText !== '' ? (
        <div className="px-4 pb-8">
          <Button
            variant={buttonVariant}
            size="lg"
            onClick={onCtaClick}
            loading={ctaLoading}
            className="w-full"
          >
            {ctaText}
          </Button>
          {ctaSubLabel != null && ctaSubLabel !== '' ? (
            <p className="font-body-3-r mt-1.5 text-center text-gray">{ctaSubLabel}</p>
          ) : null}
        </div>
      ) : null}

      {/* Features List - Only show if features exist and have items */}
      {features != null && features.length > 0 ? (
        <div className="flex flex-col gap-3 p-4 pb-10 pt-0">
          {features.map(function (feature, index) {
            return (
              <div key={index} className="flex items-center gap-2">
                <div
                  className={twMerge(
                    'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-branding-gray-100',
                    isBestValue ? 'bg-branding-accent-50' : ''
                  )}
                >
                  <Check
                    className={twMerge('flex-shrink-0', checkmarkColorClass)}
                    width={12}
                    height={12}
                  />
                </div>
                <span className="font-body-2-r text-dark-gray">{feature}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
